import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import env from '../config/env';
import { useEncryption } from './EncryptionContext';

interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface MoodTrend {
  date: string;
  average_mood: number;
  entry_count: number;
  mood_labels: string[];
}

interface HabitStat {
  id: string;
  title: string;
  measurement_type: string;
  total_entries: number;
  average_value: number;
  completed_count: number;
}

interface TagStat {
  id: string;
  encrypted_name: string;
  decrypted_name: string;
  color: string;
  entry_count: number;
  average_mood: number;
}

interface TagCoOccurrence {
  tag1_id: string;
  tag2_id: string;
  occurrence_count: number;
}

interface LocationStat {
  location_lat: number;
  location_lng: number;
  entry_count: number;
  average_mood: number;
  weather_data: any[];
}

interface WeatherStat {
  weather_condition: string;
  occurrence_count: number;
  average_mood: number;
}

interface AnalyticsContextType {
  dateRange: DateRange | null;
  setDateRange: (range: DateRange) => void;
  moodTrends: MoodTrend[];
  habitStats: HabitStat[];
  tagStats: {
    tags: TagStat[];
    coOccurrences: TagCoOccurrence[];
  };
  locationStats: {
    locations: LocationStat[];
    weatherStats: WeatherStat[];
  };
  isLoading: boolean;
  error: string | null;
  fetchAnalytics: () => Promise<void>;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [moodTrends, setMoodTrends] = useState<MoodTrend[]>([]);
  const [habitStats, setHabitStats] = useState<HabitStat[]>([]);
  const [tagStats, setTagStats] = useState<{ tags: TagStat[]; coOccurrences: TagCoOccurrence[] }>({
    tags: [],
    coOccurrences: [],
  });
  const [locationStats, setLocationStats] = useState<{
    locations: LocationStat[];
    weatherStats: WeatherStat[];
  }>({
    locations: [],
    weatherStats: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { decrypt } = useEncryption();

  const fetchAnalytics = useCallback(async () => {
    if (!dateRange) return;

    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const params = {
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
      };

      const [moodRes, habitRes, tagRes, locationRes] = await Promise.all([
        axios.get(`${env.API_URL}/analytics/mood-trends`, { headers, params }),
        axios.get(`${env.API_URL}/analytics/habit-stats`, { headers, params }),
        axios.get(`${env.API_URL}/analytics/tag-stats`, { headers, params }),
        axios.get(`${env.API_URL}/analytics/location-stats`, { headers, params }),
      ]);

      setMoodTrends(moodRes.data);
      setHabitStats(habitRes.data);

      // Decrypt tag names in tag stats
      const decryptedTags = await Promise.all(
        tagRes.data.tags.map(async (tag: TagStat) => {
          try {
            const decrypted_name = await decrypt(tag.encrypted_name);
            return { ...tag, decrypted_name };
          } catch (error) {
            console.error('Error decrypting tag name:', error);
            return { ...tag, decrypted_name: 'Error decrypting tag name' };
          }
        })
      );

      setTagStats({
        tags: decryptedTags,
        coOccurrences: tagRes.data.coOccurrences,
      });

      setLocationStats(locationRes.data);
    } catch (err) {
      setError('Failed to fetch analytics data');
      console.error('Error fetching analytics:', err);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, decrypt]);

  return (
    <AnalyticsContext.Provider
      value={{
        dateRange,
        setDateRange,
        moodTrends,
        habitStats,
        tagStats,
        locationStats,
        isLoading,
        error,
        fetchAnalytics,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}; 